import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  booleanAttribute,
  inject,
} from '@angular/core';
import {
  AbstractControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { NgIf } from '@angular/common';
import { MatTooltip } from '@angular/material/tooltip';
import { IconComponent } from '../../../icons/icon.component';

/**
 * Input (text, number, password, ...), textarea, select
 *     <app-input-wrapper [label]="'label'" [control]="form.controls.name">
 *         <input formControlName="name" placeholder="abc">
 *     </app-input-wrapper>
 *
 * Input (checkbox)
 *     <app-input-wrapper [label]="'label'" [control]="eventForm.controls.check">
 *         <input id="check" type="checkbox" formControlName="check">
 *         <label for="check">Text</label>
 *     </app-input-wrapper>
 *
 * Input (radio)
 *     <app-input-wrapper [label]="'label'" [control]="eventForm.controls.radio">
 *         <input id="radio1" value="radio1" type="radio" formControlName="radio">
 *         <label for="radio1">Text</label>
 *     </app-input-wrapper>
 *     <app-input-wrapper [control]="eventForm.controls.radio">
 *         <input id="radio2" value="radio2" type="radio" formControlName="radio">
 *         <label for="radio2">Text</label>
 *     </app-input-wrapper>
 */

@Component({
  selector: 'app-input-wrapper',
  templateUrl: './input-wrapper.component.html',
  imports: [NgIf, MatTooltip, IconComponent],
  styleUrls: ['./input-wrapper.component.scss'],
})
export class InputWrapperComponent implements OnChanges {
  private _translate = inject(TranslateService);

  @Input() label?: string;
  @Input() unit?: string;
  @Input() hint?: string;
  @Input({ transform: booleanAttribute }) isSelect: boolean = false;
  @Input({ required: true }) control!: AbstractControl;
  @Input() options?: {
    requiredMarker?: string;
    rowStyle?: boolean;
  };

  private defaultOptions: {
    requiredMarker?: string;
    rowStyle?: boolean;
  } = {
    requiredMarker: '*',
    rowStyle: false,
  };

  private _options: {
    requiredMarker?: string;
    rowStyle?: boolean;
  } = this.defaultOptions;

  get requiredMarker(): string {
    if (!this.control.hasValidator(Validators.required)) return '';
    return this._options.requiredMarker ?? '';
  }

  get rowStyle(): boolean {
    return this._options.rowStyle ?? false;
  }

  get hasErrors(): boolean {
    return this.control.invalid;
  }

  get shouldPrintError(): boolean {
    return this.hasErrors && (this.control.dirty || this.control.touched);
  }

  get renderErrors(): RenderedError[] {
    if (!this.shouldPrintError) {
      return [];
    }

    const result: RenderedError[] = [];
    const errors: ValidationErrors | null | undefined = this.control.errors;

    if (errors) {
      Object.keys(errors).forEach((keyError) => {
        result.push({
          error: keyError,
          message: this.getMessage(keyError, errors),
          value: errors![keyError],
        });
      });
    }

    return result;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['options']) {
      this._options = { ...this.defaultOptions, ...this.options };
    }
  }

  getMessage(keyError: string, errors: ValidationErrors): string {
    switch (keyError) {
      case 'required':
        return this._translate.instant('error.required');
      case 'max':
        return this._translate.instant('error.max', {
          max: errors![keyError].max,
        });
      case 'min':
        return this._translate.instant('error.min', {
          min: errors![keyError].min,
        });
      case 'minlength':
        return this._translate.instant('error.minLength', {
          min: errors![keyError].requiredLength,
        });
      case 'maxlength':
        return this._translate.instant('error.maxLength', {
          max: errors![keyError].requiredLength,
        });
      case 'pattern':
        return this._translate.instant('error.pattern', {
          pattern: errors![keyError].requiredPattern,
        });
      case 'maxFileSize':
        return this._translate.instant('error.maxFileSize', {
          max: errors![keyError].maxSize,
        });
      case 'invalidDateRange':
        return this._translate.instant('error.invalidDateRange');
      case 'invalidMultiSelection':
        return this._translate.instant('error.invalidMultiSelection');
      case 'greaterThanMin':
        return this._translate.instant('error.greaterThanMin');
      default:
        return this._translate.instant('error.default');
    }
  }
}

interface RenderedError {
  error: string;
  message: any;
  value: any;
}

export function isSubmitAllowed(form: FormGroup): boolean {
  if (form.valid) {
    return true;
  }

  showFormErrors(form);
  return false;
}

export function showFormErrors(form: FormGroup): void {
  if (form.valid) {
    return;
  }

  form.markAllAsTouched();
  window.scroll(0, 0);
}
